// Vendors
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { debounce } from 'lodash';

// Components
import { Loader, BuildersDropdown } from '../../../components';
import { BulkMessage, BulkStatusChange, ProjectCard } from './components/index';

// Helpers
import { filterProjects } from '../../../components/helpers';

// Keep outside of function, or will get overwritten at re-render.
const _PROJECTSCACHE = [];

const AllProjects = () => {
  const adminFilters = localStorage.getItem('adminFilters')
    ? JSON.parse(localStorage.getItem('adminFilters'))
    : {
        status: '',
        builder: 0,
        state: '',
        sort: 'recent_desc'
      };
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ ...adminFilters });
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilter, setShowFilter] = useState('builder');
  const [mainNotice, setMainNotice] = useState({ type: '', message: '' });
  const [showBulkStatusChange, setShowBulkStatusChange] = useState(false);
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const { builders, usStates } = window.wlPipAdmin;
  const debounceTime = 50;
  const applicableYears = window.wlPipAdmin.applicableYears;

  const storeFiltersInLocalStorage = () => {
    localStorage.setItem('adminFilters', JSON.stringify({ ...filter }));
  };

  const handleFilterChange = (property, value) => {
    const updatedFilter = { ...filter };
    updatedFilter[property] = value;

    // Reset filter if it's not visible.
    if (showFilter === 'builder') {
      updatedFilter['state'] = '';
    } else {
      updatedFilter['builder'] = 0;
    }

    setFilter(updatedFilter);
  };

  const handleFilterProjects = useCallback(
    debounce(
      (_PROJECTSCACHE, filter, searchQuery) =>
        setProjects(filterProjects(_PROJECTSCACHE, filter, searchQuery)),
      debounceTime
    ),
    []
  );

  const handleUpdateProjectStatuses = () => getProjects();

  const getProjects = () => {
    setLoading(true);

    const formData = new FormData();
    formData.append('action', 'wl_pip_admin_get_projects');

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(({ data: { success, data } }) => {
        if (success) {
          _PROJECTSCACHE.length = 0;
          _PROJECTSCACHE.push(...data);
          setProjects(filterProjects(_PROJECTSCACHE, filter));
          setMainNotice({ type: '', message: '' });
        } else {
          setProjects({});
          setMainNotice({
            type: success ? 'success' : 'error',
            message: data.message
          });
        }

        storeFiltersInLocalStorage();
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    // If cached project-list exists, use that.
    if (_PROJECTSCACHE.length) return;

    getProjects();
  }, []);

  useEffect(() => {
    storeFiltersInLocalStorage();
    handleFilterProjects(_PROJECTSCACHE, filter, searchQuery);
  }, [filter, searchQuery]);

  return (
    <div className="grid grid--gutter-vertical grid--row-stretch">
      {loading && <Loader />}
      <div className="grid__col-xs-12 u--padding-bottom-0">
        <div className="form__field form__field--required u--margin-0">
          <span className="u--margin-right-10">Filter by:</span>
          <label className="form__radio u--display-inline-block u--padding-left-30 u--margin-right-20">
            <input
              type="radio"
              value="builder"
              checked={showFilter === 'builder'}
              disabled={loading === true}
              onChange={(event) => setShowFilter(event.currentTarget.value)}
            />
            Builder
          </label>
          <label className="form__radio u--display-inline-block u--padding-left-30 ">
            <input
              type="radio"
              value="region"
              checked={showFilter === 'region'}
              disabled={loading === true}
              onChange={(event) => setShowFilter(event.currentTarget.value)}
            />
            Region
          </label>
        </div>
      </div>

      <div className="grid__col-xs-12 grid__col-md-6 u--padding-bottom-0">
        <div className="form__select form__select--lg u--margin-bottom-0">
          {showFilter === 'builder' && (
            <BuildersDropdown
              builders={builders}
              disabled={loading === true}
              value={filter.builder}
              onChange={(event) =>
                handleFilterChange('builder', event.target.value)
              }
            />
          )}
          {showFilter === 'region' && (
            <select
              className="form__input form__input--lg u--margin-bottom-0"
              disabled={loading === true}
              value={filter.state}
              onChange={(event) =>
                handleFilterChange('state', event.target.value)
              }
            >
              <option value="">-- Please select state --</option>
              {usStates &&
                Object.keys(usStates).map((singleState, index) => (
                  <option key={index} value={singleState}>
                    {usStates[singleState]}
                  </option>
                ))}
            </select>
          )}
        </div>
      </div>

      <div className="grid__col-xs-12 grid__col-md-6 u--padding-bottom-0">
        <div className="form__select form__select--lg u--padding-bottom-0">
          <select
            className="form__input form__input--md u--margin-bottom-0"
            disabled={loading === true}
            value={filter.sort}
            onChange={(event) => handleFilterChange('sort', event.target.value)}
          >
            <option value="recent_desc">Recently Added (desc)</option>
            <option value="recent_asc">Recently Added (asc)</option>
            <option value="lastUpdated_desc">Last Updated (desc)</option>
            <option value="lastUpdated_asc">Last Updated (asc)</option>
          </select>
        </div>
      </div>

      <div className="grid__col-xs-12 grid__col-md-6 u--padding-bottom-0">
        <div className="form__select form__select--lg u--padding-bottom-0">
          <select
            className="form__input form__input--lg u--margin-bottom-0"
            disabled={loading === true}
            value={filter.status}
            onChange={(event) =>
              handleFilterChange('status', event.target.value)
            }
          >
            <option value="">-- All Project Statuses --</option>
            <option value="In Progress">In Progress</option>
            <option value="Submitted">Submitted</option>
            <option value="Incomplete">Incomplete</option>
            <option value="Approved">Approved</option>
            <option value="Completed">Completed</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="grid__col-xs-12 grid__col-md-6 u--padding-bottom-0">
        <div className="form__select form__select--lg u--padding-bottom-0">
          <select
            className="form__input form__input--lg u--margin-bottom-0"
            disabled={loading === true}
            value={filter.year}
            onChange={(event) => handleFilterChange('year', event.target.value)}
          >
            <option value="">-- All Years --</option>
            {applicableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid__col-xs-12 u--padding-bottom-0">
        <div className="form__field u--margin-bottom-0">
          <div className="form__input-group">
            <input
              type="text"
              name="searchQuery"
              className="form__input form__input--search-query u--padding-bottom-0"
              placeholder="Search"
              value={searchQuery}
              disabled={loading === true}
              onChange={(event) => {
                setSearchQuery(event.currentTarget.value);
                handleFilterProjects(_PROJECTSCACHE, filter, searchQuery);
              }}
            />
            {searchQuery && (
              <button
                className="btn empty-search-query"
                onClick={() => {
                  setSearchQuery('');
                  handleFilterProjects(_PROJECTSCACHE, filter, searchQuery);
                }}
              >
                <span className="empty-search-query__icon" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid__col-xs-12">
        <div className="grid">
          <div className="grid__col-xs-6 u--padding-top-0 u--text-align-left">
            {adminFilters.status == '' ? (
              <em style={{ opacity: 0.5, fontSize: '14px' }}>
                (Select Status to send bulk message)
              </em>
            ) : (
              <button
                className={`btn btn--xs ${
                  showBulkMessage ? 'btn--outline' : 'btn--gold'
                }`}
                onClick={() => setShowBulkMessage(!showBulkMessage)}
              >
                <span className="btn__content">
                  <span className="btn__text">
                    {showBulkMessage ? 'Close' : 'Send Bulk Message'}
                  </span>
                </span>
              </button>
            )}
          </div>
          <div className="grid__col-xs-6 u--padding-top-0 u--text-align-right">
            {adminFilters.builder == 0 ? (
              <em style={{ opacity: 0.5, fontSize: '14px' }}>
                (Select Builder to make bulk status changes)
              </em>
            ) : (
              <button
                className={`btn btn--xs ${
                  showBulkStatusChange ? 'btn--outline' : 'btn--gold'
                }`}
                onClick={() => setShowBulkStatusChange(!showBulkStatusChange)}
              >
                <span className="btn__content">
                  <span className="btn__text">
                    {showBulkStatusChange ? 'Close' : 'Bulk Status Change'}
                  </span>
                </span>
              </button>
            )}
          </div>
          {showBulkStatusChange && (
            <BulkStatusChange
              projects={projects}
              adminFilters={adminFilters}
              handleUpdateProjectStatuses={handleUpdateProjectStatuses}
              setMainNotice={setMainNotice}
            />
          )}
          {showBulkMessage && (
            <BulkMessage
              projects={projects}
              adminFilters={adminFilters}
              handleUpdateProjectStatuses={handleUpdateProjectStatuses}
              setMainNotice={setMainNotice}
            />
          )}
        </div>
      </div>

      {mainNotice.type && mainNotice.message && (
        <div className="grid__col-xs-12 u--padding-top-0">
          <div className={`notice notice--${mainNotice.type}`} role="alert">
            {mainNotice.message}
          </div>
        </div>
      )}

      {projects && projects.length > 0 ? (
        projects.map((project, index) => (
          <ProjectCard key={index} project={project} filter={filter} />
        ))
      ) : !loading ? (
        <div className="grid__col-xs-12 u--padding-top-0">
          No{filter.status !== '' ? ` ${filter.status}` : ''} projects
          {filter.builder != '0' ? ` for builder.` : '.'}
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default AllProjects;
